import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { useLanguage, tr } from "../i18n";
import { lp } from "../components/Navbar";
import { PageHero, CTABand } from "./Shared";
import { Reveal, Stagger, StaggerItem, Kicker, SectionHead } from "../components/motion";
const STEPS = [{
  n: "01",
  title: "Initial Assessment",
  copy: "ESAIA reviews the organization's AI environment and applicable scope."
}, {
  n: "02",
  title: "Risk & Governance Assessment",
  copy: "Relevant AI systems, agents, governance structures and risks are assessed."
}, {
  n: "03",
  title: "Findings & Recommendations",
  copy: "Gaps and improvement opportunities are documented."
}, {
  n: "04",
  title: "Certification Decision",
  copy: "Where requirements are met, ESAIA certification is issued."
}, {
  n: "05",
  title: "12-Month Validity",
  copy: "The certification remains valid for 12 months, subject to the applicable certification conditions."
}, {
  n: "06",
  title: "Annual Reassessment",
  copy: "A new assessment is performed to maintain certification."
}];
function QrPattern() {
  const cells = [];
  let seed = 42;
  const rand = () => {
    seed = (seed * 9301 + 49297) % 233280;
    return seed / 233280;
  };
  for (let y = 0; y < 12; y++) {
    for (let x = 0; x < 12; x++) {
      const corner = x < 3 && y < 3 || x > 8 && y < 3 || x < 3 && y > 8;
      if (corner || rand() > 0.52) {
        cells.push(<rect key={`${x}-${y}`} x={x * 6} y={y * 6} width="5" height="5" fill="#0F172A" />);
      }
    }
  }
  return <svg width="72" height="72" viewBox="0 0 72 72" aria-label={tr("Certificate verification code")}>
      <rect width="72" height="72" fill="#fff" />
      {cells}
    </svg>;
}
export default function Certification() {
  const {
    lang
  } = useLanguage();
  return <div data-testid="certification-page">
      <PageHero kicker={tr("ESAIA Certification")} title={tr("Certification that evolves with your AI environment.")} copy={tr("ESAIA certification is an independent, voluntary assurance assessment of how your organization develops, deploys and governs artificial intelligence. It is valid for 12 months and renewed through annual reassessment.")} />

      <section className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8">
        <SectionHead kicker={tr("The process")} title={tr("Six steps to certified assurance.")} />
        <Stagger className="mt-14 grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3">
          {STEPS.map(s => <StaggerItem key={s.n}>
              <div className="group h-full border border-esaia-line bg-white p-7 transition-all duration-300 hover:border-esaia-blue/50 hover:shadow-md" data-testid={`cert-step-${s.n}`}>
                <div className="flex items-center justify-between">
                  <span className="font-serif text-4xl font-light text-esaia-blue/35 transition-colors group-hover:text-esaia-blue">{s.n}</span>
                  {s.n === "06" && <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-esaia-blue">{tr("Cycle repeats")}</span>}
                </div>
                <h3 className="mt-6 text-lg font-semibold text-esaia-ink">{tr(s.title)}</h3>
                <p className="mt-3 text-sm leading-relaxed text-esaia-muted">{tr(s.copy)}</p>
              </div>
            </StaggerItem>)}
        </Stagger>
      </section>

      <section className="border-y border-esaia-line bg-white">
        <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-12 px-4 py-20 sm:px-6 lg:grid-cols-12 lg:px-8">
          <div className="lg:col-span-5">
            <SectionHead kicker={tr("The certificate")} title={tr("Verifiable, dated and publicly checkable.")} copy={tr("Every ESAIA certificate carries a unique certification identifier, issue and expiry dates, and a defined assessment scope. Anyone can verify the status of a certificate through the public verification portal, without access to confidential audit information.")} />
            <Reveal delay={0.2} className="mt-8">
              <Link to={lp(lang, "/verify")} data-testid="verify-certificate-link" className="group inline-flex items-center gap-2 bg-esaia-blue px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-esaia-dark">{tr("Verify a Certificate")}{" "}<ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
              </Link>
            </Reveal>
          </div>
          <div className="lg:col-span-7">
            <Reveal delay={0.15}>
              <div className="border border-esaia-line bg-esaia-canvas p-4 sm:p-8">
                <div className="border border-esaia-line bg-white p-8 shadow-sm sm:p-10" data-testid="certificate-mock">
                  <div className="flex items-start justify-between border-b border-esaia-line pb-6">
                    <div>
                      <p className="font-serif text-xl font-light tracking-[0.28em] text-esaia-blue">{tr("ESAIA")}</p>
                      <p className="mt-1 font-mono text-[9px] uppercase tracking-[0.22em] text-esaia-subtle">{tr("Ethical Standards for Artificial Intelligence Assurance")}{" "}</p>
                    </div>
                    <QrPattern />
                  </div>
                  <div className="py-7">
                    <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-esaia-subtle">{tr("Certificate of Assurance")}</p>
                    <p className="mt-3 font-serif text-2xl font-light text-esaia-ink sm:text-3xl">{tr("Veritas Logistics NV")}</p>
                    <p className="mt-1 text-sm text-esaia-muted">{tr("Single-Entity AI Assessment · ESAIA 01:202601")}</p>
                  </div>
                  <div className="grid grid-cols-2 gap-5 border-t border-esaia-line pt-6 sm:grid-cols-4">
                    {[["Certificate ID", "ESAIA-2026-0001"], ["Issued", "02 Mar 2026"], ["Valid until", "02 Mar 2027"], ["Entities", "4"]].map(([k, v]) => <div key={k}>
                        <p className="font-mono text-[9px] uppercase tracking-[0.2em] text-esaia-subtle">{tr(k)}</p>
                        <p className="mt-1.5 font-mono text-xs font-medium text-esaia-ink">{v}</p>
                      </div>)}
                  </div>
                  <p className="mt-6 border-t border-esaia-line pt-4 text-[10px] leading-relaxed text-esaia-subtle">{tr("Sample certificate for illustration. Verify any ESAIA certificate at esaia.org using its certification identifier.")}{" "}</p>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <CTABand title={tr("Begin your certification journey.")} copy={tr("Start with the free ERI to understand your readiness, or request a quotation for a Single-Entity or Group-Wide AI Assessment.")} primary={{
      to: "/contact",
      label: tr("Request a Quote")
    }} secondary={{
      to: "/eri",
      label: tr("Take the Free ERI")
    }} />
    </div>;
}
